// MyProcessCreate.js
import React, { useCallback, useEffect, useState, useRef } from 'react';
import useFetch from '../../hooks/useFetch';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../layouts/MainLayout';
import { GenerateThink5whysProcessCode } from '../../components/GenerateThink5whysCode';

const MyProcessCreate = () => {
  const authState = useSelector(state => state.authReducer);
  const [fetchData] = useFetch();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [createSuccess, setCreateSuccess] = useState(false); 
  const [updateSuccess, setUpdateSuccess] = useState(false); 

  const [createSuccessMsg, setCreateSuccessMsg] = useState('');
  const [updateSuccessMsg, setUpdateSuccessMsg] = useState(''); 
 
  const [creatingProcess, setCreatingProcess] = useState(false); 
   
  const [verifyParentProcessSuccess, setVerifyParentProcessSuccess] = useState(false); 

  const [errors, setErrors] = useState({});
  
  const [parentProcessCreatorName, setParentProcessCreatorName] = useState(''); 
  const [autoProcessCode, setAutoTaskCode] = useState(GenerateThink5whysProcessCode());

  const userId = authState.user._id;
  const [formData, setFormData] = useState({
    createdBy: authState.user._id,
    creatorCode: authState.user.think5whyscode,
    creatorName: authState.user.name,
    processType: 'FRI', // Initialize with appropriate default or leave empty
    processCode: autoProcessCode, 
    processTitle: '',
    processDesc: '',
    processDos: '',
    processDont: '',
    processMicroPage: '',
    myProcessRemarks: '',
    dateCreated: new Date(),
    isActive: 1,
    isDeleted: 0,
    masterProcessCode: '',
    masterProcessTitle: '',
    masterProcessCreator: '',
    isLevel: false,
    entityCode: '',
    myProcessMembers: null,
  });

  const [createClicked, setCreateClicked] = useState(false);

  const handleCreateClick = () => {
    if(formData.processTitle==='')
      {
        errors.processTitle="Title Must Not Be Blank";
        return;
      }
    setCreatingProcess(true);
    fetchProcessCreate();
  };

  const fetchProcessCreate = useCallback(() => {
    // console.log('userId:', userId);
  
    try {
      const config = {
        url: "/myprocess",
        method: 'post',
        data: formData,
        headers: { Authorization: authState.token },
      };
  
      // console.log('Creating process with config:', config);
  
      fetchData(config)
        .then(response => {
          if (response && response.status) {
            // console.log("Process created successfully:", response);
            setCreateSuccess(true);
            setCreateSuccessMsg("Process Created Successfully");
            errors.processTitle='';
          } else {
            console.error("Failed to create profile:", response);
            setCreateSuccessMsg("Sorry! Profile Not Created. Please retry.");
          }
        })
        .catch(error => {
          console.error("Error creating profile:", error);
          setCreateSuccessMsg("Sorry! Profile Not Created. Please retry.");
        });
    } catch (error) {
      console.error('Error in fetchProfileCreate:', error);
      setCreateSuccessMsg("Sorry! Profile Not Created. Please retry.");
    }
  }, [userId, formData, authState, fetchData, setCreateSuccess, setCreateSuccessMsg]);

  useEffect(() => {
    if (!authState.isLoggedIn) return;
}, [authState.isLoggedIn, fetchProcessCreate, setCreateSuccess, setCreateSuccessMsg]);


const handleVerifyParentProcessClick = () => {
    // console.log("handleVerifyParentProcessClick", formData);

    setVerifyParentProcessSuccess(true);
    
    // Validate process Code
    if (/^TT[a-zA-Z0-9]{12}$/.test(formData.masterProcessCode)) {
        if (formData.masterProcessCode === formData.processCode) {
            setErrors(`Master Process Code is ${formData.masterProcessCode} and Your Process Code is ${formData.processCode}. Both must be different!`);
            return;
          } 
          fetchParentProcessCode(formData.processCode);
    } else {
        setErrors('Please enter a valid 14-character Think5Whys Process Code starting with TT.');
    }
};


const fetchParentProcessCode = useCallback(() => {
  // console.log("formData.masterProcessCode for API call ", formData.masterProcessCode);
  const config = {
      url: "/myprocess/verifyprocesscode",
      method: "post",
      headers: { Authorization: authState.token },
      data: { processCode: formData.masterProcessCode }
  };

  fetchData(config)
      .then(response => {
        // console.log("API Response: ", response); 
          if (response && response.status) {
              // console.log("formData.masterProcessCode found ", response);
              setParentCodeStatus(true);
              setParentProcessTitle(response.processcodeinfo.processTitle);
              setParentProcessCreatorName(response.processcodeinfo.creatorName);
              setParentProcessStatusMsg("Parent Process Code found. 😊");
              // console.log("Payee Code found");
          } else {
              // console.log("Payee Code Not found");
              setParentProcessStatusMsg(`Parent Process Code ${formData.masterProcessCode} not found! 😔 Make sure the Parent Process Code is valid.`);
          }
      })
      .catch(error => {
          // console.log("Parent Process Code Not found!", error);
          setParentProcessStatusMsg("Error in getting data");
      });
}, [fetchData, authState.token]);


useEffect(() => {
  if (!authState.isLoggedIn) return;
}, [authState.isLoggedIn, fetchParentProcessCode]);


  const handleBackToMyGroupsListClick = () => {
    navigate('/myprofile-processes');
  };

  const handleUpdateClick = async () => {
    try {
      const response = await fetch(`/myprocess/${formData._id}`, {
        method: 'put',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      if (response.ok) {
        // Update state or handle success as needed
        // console.log('Process updated successfully.');
      } else {
        console.error('Failed to update process:', response.statusText);
      }
    } catch (error) {
      console.error('Error updating process:', error.message);
    }
  };

  return (
    <MainLayout>
      <div className='text-lg mt-12 mx-8 border-b border-b-gray-300 text-white'>
        <h2>Profile - Process / Groups Creation</h2>
      </div>
      <div className="m-auto my-2 p-2 border-2 shadow-md rounded-md md:w-7/12 w-full text-white text-sm">
        <div className="flex flex-col justify-left mt-2 items-center">
          <div>
            <p className='text-sm text-yellow-200 mr-2'>Think5Whys Process Code: {autoProcessCode} </p>
          </div>
          <div>
            <p className='text-sm text-white mb-1 mt-2'>Input Process Title: </p>
          </div>          
          <div className='w-6/12 flex flex-row mt-2'>
            <input
              type="text"
              className="profile-input text-black"
              style={{
                border: '1px solid #ccc',
                padding: '8px',
                width: '100%',
                boxSizing: 'border-box',
              }} 
              value={formData.processTitle}
              onChange={(e) => setFormData({ ...formData, processTitle: e.target.value })}
              placeholder="Enter process title"
            />
          </div>
          <div>
            <button
              className='bg-purple-700 mt-2'
              onClick={handleCreateClick}
              disabled={createSuccess}
              style={{ maxWidth: '150px' }}
            >
              Create Process
            </button>
            <br/>
            {errors.processTitle !== '' && (
              <p className='text-red-500 text-lg'>{errors.processTitle}</p>
            )}
          </div>
        </div>
        {createSuccess && (
          <div className='text-sm'>
            <div className="mt-2 border-t">
              <p className='text-lg text-green-400'>Process {formData.processTitle} Created Successfully!</p>
              <p className='text-lg text-orange-400'>Fill the other details!</p>
            </div>
            <div className="flex flex-col mt-1 text-sm">
              <label>Process Type:</label>
              <select className="profile-select text-black" value={formData.processType} onChange={(e) => setFormData({ ...formData, processType: e.target.value })}>
                <option value={"TEA"}>Team</option>
                <option value={"DEP"}>Departmental</option>
                <option value={"BRA"}>Branch</option>
                <option value={"ZON"}>Zonal</option>
                <option value={"REG"}>Regional</option>
                <option value={"COM"}>Company Wide</option>
                <option value={"INS"}>Institutional</option>
                <option value={"ADM"}>Administartive</option>
                <option value={"MIN"}>Ministry</option>
                <option value={"PRO"}>Professional</option>
                <option value={"ACA"}>Academic</option>
                <option value={"MAN"}>Management</option>
                <option value={"OTH"}>Others</option>

              </select>
              {errors.processType && <span className="error">{errors.processType}</span>}
          </div>
          <div className="flex flex-col">
            <label>Process Description</label>
            <textarea
              className="profile-textarea"
              style={{
                border: '1px solid #ccc',
                padding: '2px',
                backgroundColor: '#f0f0f0', 
                color: '#000000',
                maxWidth: '650px', 
                maxHeight: '150px' 
              }}
              value={formData.processDesc}
              onChange={(e) => setFormData({ ...formData, processDesc: e.target.value })}
              placeholder="Enter process description"
            />
          </div>
          <div className="flex flex-col">
            <label>Process Do's</label>
            <textarea
              className="profile-textarea"
              style={{
                border: '1px solid #ccc',
                padding: '2px',
                backgroundColor: '#f0f0f0', 
                color: '#000000',
                maxWidth: '650px', 
                maxHeight: '150px' 
              }}
              value={formData.processDos}
              onChange={(e) => setFormData({ ...formData, processDos: e.target.value })}
              placeholder="Enter process do's"
            />
          </div>
          <div className="flex flex-col">
            <label>Process Don'ts</label>
            <textarea
              className="profile-textarea"
              style={{
                border: '1px solid #ccc',
                padding: '2px',
                backgroundColor: '#f0f0f0', 
                color: '#000000',
                maxWidth: '650px', 
                maxHeight: '150px' 
              }}
              value={formData.processDont}
              onChange={(e) => setFormData({ ...formData, processDont: e.target.value })}
              placeholder="Enter process don'ts"
            />
          </div>
          <div className="flex flex-col">
            <label>Micro Page Link</label>
            <input
              type="text"
              className="profile-input"
              style={{
                border: '1px solid #ccc',
                padding: '2px',
                backgroundColor: '#f0f0f0', 
                color: '#000000',
                width: '90%'
              }}
              value={formData.processMicroPage}
              onChange={(e) => setFormData({ ...formData, processMicroPage: e.target.value })}
              placeholder="Enter micro page link"
            />
          </div>
          <div className="flex flex-col">
            <label>Process Remarks</label>
            <textarea
              className="profile-textarea"
              style={{
                border: '1px solid #ccc',
                padding: '2px',
                backgroundColor: '#f0f0f0', 
                color: '#000000',
                maxWidth: '650px', 
                maxHeight: '150px' 
              }}
              value={formData.myProcessRemarks}
              onChange={(e) => setFormData({ ...formData, myProcessRemarks: e.target.value })}
              placeholder="Enter process remarks"
            />
          </div>
          <div className="flex flex-col">
            <p>Parent Process</p>
            <input
              type="text"
              className="profile-input"
              style={{
                border: '1px solid #ccc',
                padding: '2px',
                backgroundColor: '#f0f0f0', 
                color: '#000000',
                width: '150px'
              }}
              value={formData.masterProcessCode}
              onChange={(e) => setFormData({ ...formData, masterProcessCode: e.target.value })}
              placeholder="Enter parent process code"
            />
            {/* <button
              className='bg-orange-700 mt-2'
              onClick={handleVerifyParentProcessClick}
              disabled={verifyParentProcessSuccess}
              style={{ maxWidth: '250px' }}
            >
              Verify Parent Process
            </button> */}
          </div>
          <div className="flex flex-col">
              <label>
              <input className='mr-4' type="checkbox" checked={formData.isLevel} onChange={(e) => setFormData({ ...formData, isLevel: e.target.checked })} />
              Process Has Hierarchy?
              </label>
          </div>
          <div className="flex justify-center mt-4">
            <button
              className='bg-purple-700'
              onClick={handleUpdateClick}
              style={{ maxWidth: '150px' }}
            >
              Update
            </button>
            <button
              className='bg-purple-700 ml-4'
              onClick={handleBackToMyGroupsListClick}
              style={{ maxWidth: '250px' }}
            >
              Back To My Process / Group List
            </button>
          </div>
        </div>
        )}
      </div>
    </MainLayout>
  );
};

export default MyProcessCreate;
